import { defineStore } from 'pinia';
import merge from 'lodash/merge';
import { httpService, langService, reportService, toastService } from '@/core';
import { translationUrl } from '@/api/sources';
import { userInfoUrl, userPulseUrl } from '@/api/user';
import { loginUrl } from '@/api/auth';
import { User } from '@/models/user';
import PrettyConsole from '@/utils/pretty-console';
import { usePulse } from '@/composables/pulse';
import { useUpdateStore } from '@/stores/update';
import { differenceInDays } from 'date-fns';
import { useForm } from '@/composables/form';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isAdmin: false,
    user: null,
    notifications: [],
    company: null,
    otherCompanies: [],
    subscription_mismatch: false,
    locale: null,
    twoFactorChallengeLoading: false,
    twoFactorChallengeModalVisible: false,
    newDeviceChallengeModalVisible: false,
    twoFactorChallengeCompleted: false,
    twoFactorRequired: false,
    twoFactorToggleLoading: false,
    twoFactorConfirmed: false,
    twoFactorConfirmModalVisible: false,
    twoFactorConfirmLoading: false,
    twoFactorQrCode: null,
    twoFactorRecoveryCodes: [],
    passwordConfirmModalVisible: false,
    passwordConfirmLoading: false,
    confirmEmailLoading: false,
    confirmEmailSent: false,
    contact_email: null,
    twoFactorChallengeForm: useForm({
      code: null,
      use_recovery_code: false,
    }),
    confirmPasswordForm: useForm({
      password: null,
    }),
    confirmTwoFactorForm: useForm({
      code: null,
      two_factor_type: null,
    }),
  }),

  getters: {
    hasAnyCompany() {
      return Boolean(this.company) || this.otherCompanies.length > 0;
    },

    isLoggedIn() {
      return this.user !== null;
    },

    isImpersonating() {
      return this.user !== null && this.user.isImpersonating;
    },

    isSuperAdmin() {
      return this.user !== null && this.user.is_super;
    },

    fullName() {
      if (!this.user) {
        return '';
      }

      return `${this.user.profile.name} ${this.user.profile.last_name}`;
    },

    expirationDays() {
      const expiration = this.user?.permissions?.user_subscription?.expiration;

      if (!expiration) {
        return null;
      }

      return differenceInDays(new Date(expiration), new Date());
    },

    companyCount() {
      let count = this.otherCompanies.filter(it => it.owner).length;

      if (this.company && this.company.owner) {
        count++;
      }

      return count;
    },

    hasUnreadNotification() {
      return this.notifications.some(it => it.seen === null);
    },

    unreadNotificationsLength() {
      return this.notifications.filter(it => it.seen === null).length;
    },

    hasConfirmedEmail() {
      return Boolean(this.user.profile.email_confirmed);
    },

    onboardingCompleted() {
      return Boolean(this.user.profile.onboarding_completed);
    },

    trialExpired() {
      return Boolean(this.user.permissions.subscription.tag === 'free' && this.user.permissions.subscription.is_trial_user);
    },

    twoFactorEnabled() {
      return Boolean(this.user.profile.two_factor_enabled);
    },
  },

  actions: {
    setUser(payload) {
      this.user = payload;
      this.isAdmin = true;
    },

    setCompany(payload) {
      this.company = payload;
    },

    removeCompany(payload) {
      this.otherCompanies = this.otherCompanies.filter(it => it.id !== payload);
    },

    setOtherCompanies(payload) {
      this.otherCompanies = payload;
    },

    updateUser(payload) {
      merge(this.user, payload);
    },

    updateActiveCompany(payload) {
      Object.assign(this.company, payload);
    },

    setNotifications(payload) {
      this.notifications = payload;
    },

    setUserSubscription(payload) {
      this.user.permissions.user_subscription = payload;
    },

    setSubscriptionMismatch(value) {
      this.subscription_mismatch = value;
    },

    markUserNotificationsSeen() {
      this.notifications.forEach(it => {
        it.seen = new Date().toISOString()
          .substring(0, 19)
          .replace('T', '');
      });
    },

    setUserAgreements(payload) {
      this.user.profile.gdpr = payload;
    },

    setSaveExternalClientsNotice(payload) {
      this.company.save_external_clients_notice = payload;
    },

    updateUserModules(payload) {
      const enabledModule = this.user.activeCompany.enabled_modules.filter(m => m?.type === payload.data.type)[0];

      if (enabledModule) {
        Object.assign(enabledModule, payload.data);
      } else {
        this.user.activeCompany.enabled_modules.push(payload.data);
      }
    },

    setLocale(payload) {
      this.locale = payload;
    },

    setTwoFactorEnabled(payload) {
      this.user.profile.two_factor_enabled = payload;
    },

    setTwoFactorRequired(payload) {
      this.two_factor_required = payload;
    },

    setConfirmPasswordModalVisible(payload) {
      this.passwordConfirmModalVisible = payload;
    },

    setConfirmPasswordLoading(payload) {
      this.passwordConfirmLoading = payload;
    },

    setTwoFactorToggleLoading(payload) {
      this.twoFactorToggleLoading = payload;
    },

    setTwoFactorQrCode(payload) {
      this.twoFactorQrCode = payload;
    },

    setTwoFactorConfirmModalVisible(payload) {
      this.twoFactorConfirmModalVisible = payload;
    },

    setTwoFactorRecoveryCodes(payload) {
      this.twoFactorRecoveryCodes = payload;
    },

    setTwoFactorConfirmLoading(payload) {
      this.twoFactorConfirmLoading = payload;
    },

    setTwoFactorConfirmed(payload) {
      this.twoFactorConfirmed = payload;
    },

    setTwoFactorChallengeModalVisible(payload) {
      this.twoFactorChallengeModalVisible = payload;
    },

    setTwoFactorChallengeLoading(payload) {
      this.twoFactorChallengeLoading = payload;
    },

    setTwoFactorChallengeCompleted(payload) {
      this.twoFactorChallengeCompleted = payload;
    },

    setConfirmEmailLoading(payload) {
      this.confirmEmailLoading = payload;
    },

    setConfirmEmailSent(payload) {
      this.confirmEmailSent = payload;
    },

    setNewDeviceChallengeModalVisible(payload) {
      this.newDeviceChallengeModalVisible = payload;
    },

    setContactEmail(payload) {
      this.contact_email = payload;
    },

    async pulse() {
      if (!this.user) {
        return;
      }

      try {
        const response = await httpService.get(userPulseUrl());
        this.setNotifications(response.data?.data?.notifications);
      } catch (e) {
        reportService.report(e);
      }
    },

    async markNotificationsSeen() {
      console.info('Skipping notification mark as seen.');
    },

    async boot() {
      try {
        const response = await httpService.get(userInfoUrl());
        this.setUser(new User(response.data.data));
        this.setCompany(response.data.data.activeCompany);
        this.setOtherCompanies(response.data.data.otherCompanies);
        useUpdateStore().setShowWoocommerceUpdateModal(response.data.data.triggerWoocommerceUpdateModal);

        usePulse();

        return response;
      } catch (e) {
        if (e.response && e.response.status === 401) {
          PrettyConsole.debug('auth', 'User unauthenticated.');
        } else {
          reportService.report(e);
        }
      }

      return null;
    },

    async clear() {
      this.setUser(null);
      this.setCompany(null);
      this.setOtherCompanies([]);
    },

    async login(payload) {
      const response = await httpService.post(loginUrl(), payload);
      const langResponse = await httpService.get(translationUrl());
      await langService.load(langResponse.data.data.locale, langResponse.data.data.translations);

      return response;
    },

    async updateModules(payload) {
      this.updateUserModules(payload);
    },

    async toggleTwoFactor() {
      try {
        this.setTwoFactorToggleLoading(true);

        if (this.twoFactorEnabled) {
          await httpService.delete('/user/two-factor-authentication');
          this.setTwoFactorEnabled(false);
        } else {
          await httpService.post('/user/two-factor-authentication');
          this.setTwoFactorConfirmModalVisible(true);

          this.getTwoFactorQrCode();
        }
      } catch (e) {
        if (e.response?.data?.message === 'Password confirmation required.') {
          this.setConfirmPasswordModalVisible(true);
        } else {
          toastService.fromResponse(e);
        }
      } finally {
        this.setTwoFactorToggleLoading(false);
      }
    },

    async confirmTwoFactor() {
      try {
        this.setTwoFactorConfirmLoading(true);
        await httpService.post('/user/confirmed-two-factor-authentication', {
          data: {
            code: this.confirmTwoFactorForm.$.code,
            two_factor_type: this.confirmTwoFactorForm.$.two_factor_type,
          },
        });

        this.setTwoFactorEnabled(true);
        this.setTwoFactorConfirmed(true);
        this.setTwoFactorConfirmModalVisible(false);
        toastService.success({
          text: langService.trans('auth.feedback.two_factor_enabled'),
        });
      } catch (e) {
        this.confirmTwoFactorForm.$e.updateFromError(e);
        toastService.fromResponse(e);
      } finally {
        this.setTwoFactorConfirmLoading(false);
      }
    },

    async processTwoFactorChallenge() {
      try {
        this.setTwoFactorChallengeLoading(true);

        await httpService.post('/two-factor-challenge', {
          data: {
            code: this.twoFactorChallengeForm.$.code,
          },
        });

        this.setTwoFactorChallengeModalVisible(false);
        this.setTwoFactorChallengeLoading(false);
        this.setTwoFactorChallengeCompleted(true);
      } catch (e) {
        this.twoFactorChallengeForm.$e.updateFromError(e);
        toastService.fromResponse(e);
        this.setTwoFactorChallengeLoading(false);
      }
    },

    async confirmPassword() {
      try {
        this.setConfirmPasswordLoading(true);

        await httpService.post('/user/confirm-password', {
          data: {
            password: this.confirmPasswordForm.$.password,
          },
        });

        this.setConfirmPasswordModalVisible(false);
        this.toggleTwoFactor();
      } catch (e) {
        this.confirmPasswordForm.$e.updateFromError(e);
        toastService.fromResponse(e);
      } finally {
        this.setConfirmPasswordLoading(false);
      }
    },

    async getTwoFactorQrCode() {
      try {
        const response = await httpService.get('/user/two-factor-qr-code');
        this.setTwoFactorQrCode(response.data);
      } catch (e) {
        toastService.fromResponse(e);
      }
    },

    async getTwoFactorRecoveryCodes() {
      try {
        const response = await httpService.get('/user/two-factor-recovery-codes');
        this.setTwoFactorRecoveryCodes(response.data);
      } catch (e) {
        toastService.fromResponse(e);
      }
    },

    async sendTwoFactorConfirmEmail() {
      try {
        this.setConfirmEmailLoading(true);
        await httpService.post('/user/confirm-email');
        this.setConfirmEmailSent(true);
      } catch (e) {
        toastService.fromResponse(e);
      } finally {
        this.setConfirmEmailLoading(false);
      }
    },

    async resetTwoFactor() {
      try {
        await httpService.post('/rest/reset-two-factor');
      } catch (e) {
        toastService.fromResponse(e);
      }
    },
  },
});
